// src/pages/DashboardPage.tsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useData } from "../DataContext";
import { projectDisplayLabelPretty } from "../utils/format";
import { useTranslation } from "react-i18next";

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const pct = (n: number) => `${((Number(n) || 0) * 100).toFixed(2)}%`;

const safeDiv = (a: number, b: number) => {
  const aa = Number(a) || 0;
  const bb = Number(b) || 0;
  return bb === 0 ? 0 : aa / bb;
};

type StatusFilter = "All" | "Active" | "Settled";

export default function DashboardPage() {
  const { t } = useTranslation();
  const {
    clients,
    projects,
    wageEntries,
    expenses,
    subContracts,
    paymentsReceived,
    scopeItems,
  } = useData() as any;

  /* ======================
     FILTERS
  ====================== */
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("All");
  const [onlyNeedsAttention, setOnlyNeedsAttention] = useState(false);
  const [hideArchived, setHideArchived] = useState(false);

  /* ======================
     PROJECT ROWS (split costs)
  ====================== */
  const projectRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return (projects || [])
      .filter((p: any) => {
        if (hideArchived && p.archived) return false;

        if (statusFilter !== "All") {
          if (statusFilter === "Active" && p.status === "Settled") return false;
          if (statusFilter === "Settled" && p.status !== "Settled") return false;
        }

        if (q) {
          const clientName = (clients || []).find((c: any) => c.id === p.clientId)?.name || "";
          const hay = `${p.id} ${p.name || ""} ${p.address || ""} ${clientName}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }

        return true;
      })
      .map((p: any) => {
        // --- Total Estimate is scopeItems sum (do not trust p.totalEstimate blindly)
        const totalEstimate = (scopeItems || [])
          .filter((s: any) => s.projectId === p.id)
          .reduce((sum: number, s: any) => sum + (Number(s.estimate) || 0), 0);

        // --- Payments received (split cash/bank)
        const receivedCash = (paymentsReceived || [])
          .filter((pr: any) => pr.projectId === p.id && pr.paymentMethod === "Cash")
          .reduce((sum: number, pr: any) => sum + (Number(pr.amount) || 0), 0);

        const receivedBank = (paymentsReceived || [])
          .filter((pr: any) => pr.projectId === p.id && pr.paymentMethod === "Bank")
          .reduce((sum: number, pr: any) => sum + (Number(pr.amount) || 0), 0);

        const received = receivedCash + receivedBank;
        const receivable = Math.max(totalEstimate - received, 0);

        // --- Wages (paid only count as cost-to-date)
        const wagesPaid = (wageEntries || [])
          .filter((w: any) => w.projectId === p.id && !!w.paid)
          .reduce((s: number, w: any) => s + (Number(w.totalWages || 0) || 0), 0);

        const unpaidWages = (wageEntries || [])
          .filter((w: any) => w.projectId === p.id && !w.paid)
          .reduce((s: number, w: any) => s + (Number(w.totalWages || 0) || 0), 0);

        const wagesCash = (wageEntries || [])
          .filter(
            (w: any) =>
              w.projectId === p.id &&
              !!w.paid &&
              (w.paidMethod === "Cash" || !w.paidMethod)
          )
          .reduce((s: number, w: any) => s + (Number(w.totalWages || 0) || 0), 0);

        const wagesBank = (wageEntries || [])
          .filter((w: any) => w.projectId === p.id && !!w.paid && w.paidMethod === "Bank")
          .reduce((s: number, w: any) => s + (Number(w.totalWages || 0) || 0), 0);

        // --- Expenses / Subcontract payments (split cash/bank directly from expenses)
        const subContractPayments = (expenses || [])
          .filter((e: any) => e.projectId === p.id && e.expenseType === "Sub-Contract Payment")
          .reduce((s: number, e: any) => s + (Number(e.amount || 0) || 0), 0);

        const otherExpenses = (expenses || [])
          .filter((e: any) => e.projectId === p.id && e.expenseType === "Expense")
          .reduce((s: number, e: any) => s + (Number(e.amount || 0) || 0), 0);

        const subContractPaymentsCash = (expenses || [])
          .filter(
            (e: any) =>
              e.projectId === p.id &&
              e.expenseType === "Sub-Contract Payment" &&
              e.paymentMethod === "Cash"
          )
          .reduce((s: number, e: any) => s + (Number(e.amount || 0) || 0), 0);

        const subContractPaymentsBank = (expenses || [])
          .filter(
            (e: any) =>
              e.projectId === p.id &&
              e.expenseType === "Sub-Contract Payment" &&
              e.paymentMethod === "Bank"
          )
          .reduce((s: number, e: any) => s + (Number(e.amount || 0) || 0), 0);

        const otherExpensesCash = (expenses || [])
          .filter(
            (e: any) =>
              e.projectId === p.id &&
              e.expenseType === "Expense" &&
              e.paymentMethod === "Cash"
          )
          .reduce((s: number, e: any) => s + (Number(e.amount || 0) || 0), 0);

        const otherExpensesBank = (expenses || [])
          .filter(
            (e: any) =>
              e.projectId === p.id &&
              e.expenseType === "Expense" &&
              e.paymentMethod === "Bank"
          )
          .reduce((s: number, e: any) => s + (Number(e.amount || 0) || 0), 0);

        // True costs paid-to-date
        const totalCost = wagesPaid + subContractPayments + otherExpenses;

        // Profit-to-date (cash reality): money received minus money spent
        const netProfit = received - totalCost;

        const marginDenom = received > 0 ? received : totalEstimate;
        const profitMargin = safeDiv(netProfit, marginDenom);

        // outstanding sub balances (enriched in DataContext)
        const outstandingSubBalances = (subContracts || [])
          .filter((sc: any) => sc.projectId === p.id)
          .reduce((s: number, sc: any) => s + (Number(sc.balance) || 0), 0);

        const expSubCash = subContractPaymentsCash + otherExpensesCash;
        const expSubBank = subContractPaymentsBank + otherExpensesBank;

        const cashNetProfit = receivedCash - (wagesCash + expSubCash);
        const bankNetProfit = receivedBank - (wagesBank + expSubBank);

        const needsAttention =
          receivable > 0 ||
          outstandingSubBalances > 0 ||
          unpaidWages > 0 ||
          netProfit < 0;

        return {
          id: p.id,
          name: p.name,
          clientId: p.clientId,
          address: p.address,
          status: p.status,
          archived: p.archived,

          totalEstimate,
          received,
          receivable,

          wages: wagesPaid,
          subContractPayments,
          otherExpenses,

          subContractPaymentsCash,
          subContractPaymentsBank,
          otherExpensesCash,
          otherExpensesBank,

          netProfit,
          profitMargin,

          outstandingSubBalances,
          unpaidWages,

          // splits for summary + display
          receivedCash,
          receivedBank,
          wagesCash,
          wagesBank,
          expSubCash,
          expSubBank,
          cashNetProfit,
          bankNetProfit,

          needsAttention,
        };
      })
      .filter((r: any) => (onlyNeedsAttention ? r.needsAttention : true))
      .sort((a: any, b: any) => {
        const na = a.needsAttention ? 1 : 0;
        const nb = b.needsAttention ? 1 : 0;
        if (na !== nb) return nb - na;
        if (a.receivable !== b.receivable) return b.receivable - a.receivable;
        return b.totalEstimate - a.totalEstimate;
      });
  }, [
    projects,
    clients,
    wageEntries,
    expenses,
    subContracts,
    paymentsReceived,
    scopeItems,
    search,
    statusFilter,
    onlyNeedsAttention,
    hideArchived,
  ]);

  /* ======================
     ALL PROJECTS SUMMARY
  ====================== */
  const allSummary = useMemo(() => {
    const totalEstimate = projectRows.reduce((s: number, r: any) => s + r.totalEstimate, 0);
    const totalReceived = projectRows.reduce((s: number, r: any) => s + r.received, 0);
    const totalOutstanding = Math.max(totalEstimate - totalReceived, 0);

    const totalWages = projectRows.reduce((s: number, r: any) => s + r.wages, 0);
    const totalSubContracts = projectRows.reduce((s: number, r: any) => s + r.subContractPayments, 0);
    const totalExpenses = projectRows.reduce((s: number, r: any) => s + r.otherExpenses, 0);

    const totalSubContractsCash = projectRows.reduce(
      (s: number, r: any) => s + (r.subContractPaymentsCash || 0),
      0
    );
    const totalSubContractsBank = projectRows.reduce(
      (s: number, r: any) => s + (r.subContractPaymentsBank || 0),
      0
    );

    const totalExpensesCash = projectRows.reduce(
      (s: number, r: any) => s + (r.otherExpensesCash || 0),
      0
    );
    const totalExpensesBank = projectRows.reduce(
      (s: number, r: any) => s + (r.otherExpensesBank || 0),
      0
    );

    const totalCost = totalWages + totalSubContracts + totalExpenses;
    const netProfit = totalReceived - totalCost;
    const avgProfitMargin = safeDiv(netProfit, totalReceived > 0 ? totalReceived : totalEstimate);

    // cash/bank splits (DIRECT, consistent)
    const cashReceived = projectRows.reduce((s: number, r: any) => s + (r.receivedCash || 0), 0);
    const bankReceived = projectRows.reduce((s: number, r: any) => s + (r.receivedBank || 0), 0);

    const wagesCash = projectRows.reduce((s: number, r: any) => s + (r.wagesCash || 0), 0);
    const wagesBank = projectRows.reduce((s: number, r: any) => s + (r.wagesBank || 0), 0);

    const expSubCash = projectRows.reduce((s: number, r: any) => s + (r.expSubCash || 0), 0);
    const expSubBank = projectRows.reduce((s: number, r: any) => s + (r.expSubBank || 0), 0);

    const cashNetProfit = cashReceived - (expSubCash + wagesCash);
    const bankNetProfit = bankReceived - (expSubBank + wagesBank);

    return {
      totalEstimate,
      totalReceived,
      totalOutstanding,
      totalWages,
      totalSubContracts,
      totalExpenses,

      totalSubContractsCash,
      totalSubContractsBank,
      totalExpensesCash,
      totalExpensesBank,

      netProfit,
      avgProfitMargin,

      cashReceived,
      bankReceived,
      expSubCash,
      expSubBank,
      wagesCash,
      wagesBank,
      cashNetProfit,
      bankNetProfit,
    };
  }, [projectRows]);

  const chart = useMemo(() => {
    const gross = allSummary.totalEstimate || 0;
    return {
      receivedPct: safeDiv(allSummary.totalReceived, gross),
      outstandingPct: safeDiv(allSummary.totalOutstanding, gross),
      wagesPct: safeDiv(allSummary.totalWages, gross),
      subPct: safeDiv(allSummary.totalSubContracts, gross),
      expPct: safeDiv(allSummary.totalExpenses, gross),
      profitPct: safeDiv(allSummary.netProfit, gross),
    };
  }, [allSummary]);

  const barW = useMemo(() => {
    const clamp100 = (n: number) => Math.max(0, Math.min(100, n));

    // Collections (100% bar = Estimate)
    const received = clamp100((chart.receivedPct || 0) * 100);
    const outstanding = clamp100(100 - received);

    // Amount Received / Cost Breakdown (100% bar = Total Received)
    const receivedBase = Math.max(0, allSummary.totalReceived || 0);

    // Guard: if nothing received, show empty bar (all remaining)
    if (receivedBase === 0) {
      return { received, outstanding, exp: 0, sub: 0, wages: 0, profit: 0, remaining: 100, profitPct100: 0 };
    }

    const expAmt = Math.max(0, allSummary.totalExpenses || 0);
    const subAmt = Math.max(0, allSummary.totalSubContracts || 0);
    const wageAmt = Math.max(0, allSummary.totalWages || 0);

    const exp = clamp100((expAmt / receivedBase) * 100);
    const sub = clamp100(Math.min(100 - exp, (subAmt / receivedBase) * 100));
    const wages = clamp100(Math.min(100 - exp - sub, (wageAmt / receivedBase) * 100));

    // Surplus is cash-based now
    const surplusAmt = receivedBase - (expAmt + subAmt + wageAmt);
    const profitPct100 = (surplusAmt / receivedBase) * 100;

    const profit =
      surplusAmt > 0
        ? clamp100(Math.min(100 - exp - sub - wages, (surplusAmt / receivedBase) * 100))
        : 0;

    const remaining = clamp100(100 - exp - sub - wages - profit);


    return { received, outstanding, exp, sub, wages, profit, remaining, profitPct100 };
  }, [chart, allSummary.totalReceived, allSummary.totalExpenses, allSummary.totalSubContracts, allSummary.totalWages]);

  const surplusAmt =
    (allSummary.totalReceived || 0) -
    ((allSummary.totalExpenses || 0) +
      (allSummary.totalSubContracts || 0) +
      (allSummary.totalWages || 0));

  // Filters inside Projects card header (Dashboard)
  const projectsHeaderRight = (
    <div
      style={{
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        alignItems: "end",
        justifyContent: "flex-end",
      }}
    >
      <div style={{ minWidth: 180 }}>
        <label style={{ display: "block", fontSize: 12, color: "var(--pageMuted)" }}>
          Status
        </label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          style={{ width: "100%" }}
        >
          <option value="All">{t("common.all")}</option>
          <option value="Active">{t("common.active")}</option>
          <option value="Settled">{t("common.settled")}</option>
        </select>
      </div>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: "var(--pageText)",
          whiteSpace: "nowrap",
        }}
      >
        <input
          type="checkbox"
          checked={onlyNeedsAttention}
          onChange={(e) => setOnlyNeedsAttention(e.target.checked)}
        />
        {t("dashboard.needsAttentionOnly")}
      </label>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: "var(--pageText)",
          whiteSpace: "nowrap",
        }}
      >
        <input
          type="checkbox"
          checked={hideArchived}
          onChange={(e) => setHideArchived(e.target.checked)}
        />
        {t("dashboard.hideArchived")}
      </label>
    </div>
  );

  return (
    <div className="container">
      {/* Page header (title left, search top-right) */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "end",
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        <div>
          <h1 style={{ marginBottom: 0 }}>{t("dashboard.pageTitle")}</h1>
        </div>

        <div style={{ minWidth: 320, maxWidth: 520, flex: "1 1 360px" }}>
          <label style={{ display: "block", fontSize: 12, color: "var(--pageMuted)" }}>
            Search
          </label>
          <input
            className="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("dashboard.searchPlaceholder")}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      {/* All Projects Summary */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="cardHeader">
          <h3 className="cardTitle">{t("dashboard.allProjectsSummary")}</h3>
        </div>

        <div className="grid2">
          {/* Spreadsheet-style summary */}
          <div style={{ overflowX: "auto" }}>
            <div className="tableWrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>{t("dashboard.allProjects")}</th>
                    <th>{t("dashboard.cashBank")}</th>
                    <th>{t("dashboard.pctLabel")}</th>
                    <th>{t("common.cash")}</th>
                    <th>{t("common.bank")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <strong>{t("dashboard.grossSales")}</strong>
                    </td>
                    <td>{money(allSummary.totalEstimate)}</td>
                    <td>{pct(1)}</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>

                  <tr>
                    <td>{t("dashboard.amtReceived")}</td>
                    <td>{money(allSummary.totalReceived)}</td>
                    <td>{pct(safeDiv(allSummary.totalReceived, allSummary.totalEstimate))}</td>
                    <td>{money(allSummary.cashReceived)}</td>
                    <td>{money(allSummary.bankReceived)}</td>
                  </tr>

                  <tr>
                    <td>{t("dashboard.amtOutstanding")}</td>
                    <td>{money(allSummary.totalOutstanding)}</td>
                    <td>{pct(safeDiv(allSummary.totalOutstanding, allSummary.totalEstimate))}</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>

                  <tr>
                    <td>
                      <strong>{t("dashboard.subContracts")}</strong>
                    </td>
                    <td>{money(allSummary.totalSubContracts)}</td>
                    <td>{pct(safeDiv(allSummary.totalSubContracts, allSummary.totalEstimate))}</td>
                    <td>{money(allSummary.totalSubContractsCash)}</td>
                    <td>{money(allSummary.totalSubContractsBank)}</td>
                  </tr>

                  <tr>
                    <td>
                      <strong>{t("dashboard.expenses")}</strong>
                    </td>
                    <td>{money(allSummary.totalExpenses)}</td>
                    <td>{pct(safeDiv(allSummary.totalExpenses, allSummary.totalEstimate))}</td>
                    <td>{money(allSummary.totalExpensesCash)}</td>
                    <td>{money(allSummary.totalExpensesBank)}</td>
                  </tr>

                  <tr>
                    <td>
                      <strong>{t("dashboard.wages")}</strong>
                    </td>
                    <td>{money(allSummary.totalWages)}</td>
                    <td>{pct(safeDiv(allSummary.totalWages, allSummary.totalEstimate))}</td>
                    <td>{money(allSummary.wagesCash)}</td>
                    <td>{money(allSummary.wagesBank)}</td>
                  </tr>

                  <tr>
                    <td>
                      <strong>{t("dashboard.netProfit")}</strong>
                    </td>
                    <td className={allSummary.netProfit >= 0 ? "numGood" : "numBad"}>
                      {money(allSummary.netProfit)}
                    </td>
                    <td className={allSummary.avgProfitMargin >= 0 ? "numGood" : "numBad"}>
                      {pct(allSummary.avgProfitMargin)}
                    </td>
                    <td className={allSummary.cashNetProfit >= 0 ? "numGood" : "numBad"}>
                      {money(allSummary.cashNetProfit)}
                    </td>
                    <td className={allSummary.bankNetProfit >= 0 ? "numGood" : "numBad"}>
                      {money(allSummary.bankNetProfit)}
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <strong>{t("dashboard.avgProfitMargin")}</strong>
                    </td>
                    <td style={{ fontWeight: 900 }}>{pct(allSummary.avgProfitMargin)}</td>
                    <td />
                    <td style={{ fontWeight: 800 }}>
                      {pct(safeDiv(allSummary.cashNetProfit, Math.max(allSummary.cashReceived, 1)))}
                    </td>
                    <td style={{ fontWeight: 800 }}>
                      {pct(safeDiv(allSummary.bankNetProfit, Math.max(allSummary.bankReceived, 1)))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>
              {t("dashboard.cashBankNote")}
            </div>
          </div>

          {/* Chart card */}
          <div className="card" style={{ padding: 12 }}>
            {/* 1) Collections */}
            <div style={{ fontWeight: 1000, marginBottom: 10 }}>{t("dashboard.collectionsTitle")}</div>

            <div className="segLegend" style={{ marginBottom: 8 }}>
              <div className="segLegendItem">
                <span className="segSwatch segSwatchRecv" />
                <span>{t("dashboard.amtReceived")}</span>
              </div>
              <div className="segLegendItem">
                <span className="segSwatch segSwatchOut" />
                <span>{t("dashboard.amtOutstanding")}</span>
              </div>
            </div>

            <div className="segBarWrap" title={t("dashboard.estimateBarTip")}>
              <div className="segTick segTick25" />
              <div className="segTick segTick50" />
              <div className="segTick segTick75" />

              <div className="segBar">
                <div className="segSeg segRecv" style={{ width: `${barW.received}%` }}>
                  {barW.received >= 10 ? <span className="segLabel">{barW.received.toFixed(0)}%</span> : null}
                </div>
                <div className="segSeg segOut" style={{ width: `${barW.outstanding}%` }}>
                  {barW.outstanding >= 10 ? <span className="segLabel">{barW.outstanding.toFixed(0)}%</span> : null}
                </div>
              </div>
            </div>

            <div className="segAxis">
              <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
            </div>

            <div className="barAmountsRow">
              <div className="barAmt">
                <div className="barAmtLabel">{t("dashboard.amtReceived")}</div>
                <div className="barAmtValue">{money(allSummary.totalReceived)}</div>
              </div>
              <div className="barAmt">
                <div className="barAmtLabel">{t("dashboard.amtOutstanding")}</div>
                <div className="barAmtValue">{money(allSummary.totalOutstanding)}</div>
              </div>
            </div>

            <div style={{ height: 14 }} />

            {/* 2) Sales / Cost Breakdown */}
            <div style={{ fontWeight: 1000, marginBottom: 10 }}>{t("dashboard.costBreakdownTitle")}</div>

            <div className="segLegend" style={{ marginBottom: 8 }}>
              <div className="segLegendItem">
                <span className="segSwatch segSwatchExp" />
                <span>{t("dashboard.expenses")}</span>
              </div>
              <div className="segLegendItem">
                <span className="segSwatch segSwatchSub" />
                <span>{t("dashboard.subContracts")}</span>
              </div>
              <div className="segLegendItem">
                <span className="segSwatch segSwatchWage" />
                <span>{t("dashboard.wages")}</span>
              </div>
              <div className="segLegendItem">
                <span className="segSwatch segSwatchProfit" />
                <span>{t("dashboard.surplus")}</span>
              </div>
            </div>

            <div className="segBarWrap" title={t("dashboard.receivedBarTip")}>
              <div className="segTick segTick25" />
              <div className="segTick segTick50" />
              <div className="segTick segTick75" />

              <div className="segBar">
                <div className="segSeg segExp" style={{ width: `${barW.exp}%` }}>
                  {barW.exp >= 10 ? <span className="segLabel">{barW.exp.toFixed(0)}%</span> : null}
                </div>

                <div className="segSeg segSub" style={{ width: `${barW.sub}%` }}>
                  {barW.sub >= 10 ? <span className="segLabel">{barW.sub.toFixed(0)}%</span> : null}
                </div>

                <div className="segSeg segWage" style={{ width: `${barW.wages}%` }}>
                  {barW.wages >= 10 ? <span className="segLabel">{barW.wages.toFixed(0)}%</span> : null}
                </div>
                <div
                  className={`segSeg ${allSummary.netProfit >= 0 ? "segProfit" : "segLoss"}`}
                  style={{ width: `${barW.profit}%` }}
                  title={`Surplus: ${money(surplusAmt)} (${pct(safeDiv(surplusAmt, allSummary.totalReceived))})`}
                >
                  {barW.profit >= 10 ? <span className="segLabel">{barW.profit.toFixed(0)}%</span> : null}
                </div>

                {/* fills to 100% so bar always looks like your reference */}
                  {barW.remaining > 0.25 ? (
                    <div className="segSeg segRemain" style={{ width: `${barW.remaining}%` }} />
                  ) : null}
              </div>
            </div>

            <div className="segAxis">
              <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
            </div>
            
            <div className="barAmountsRow barAmountsRow4">
              <div className="barAmt">
                <div className="barAmtLabel">{t("dashboard.expenses")}</div>
                <div className="barAmtValue">{money(allSummary.totalExpenses)}</div>
              </div>
              <div className="barAmt">
                <div className="barAmtLabel">{t("dashboard.subContracts")}</div>
                <div className="barAmtValue">{money(allSummary.totalSubContracts)}</div>
              </div>
              <div className="barAmt">
                <div className="barAmtLabel">{t("dashboard.wages")}</div>
                <div className="barAmtValue">{money(allSummary.totalWages)}</div>
              </div>
              <div className="barAmt">
                <div className="barAmtLabel">{surplusAmt >= 0 ? t("dashboard.surplus") : t("dashboard.deficit")}</div>
                <div className="barAmtValue">{money(surplusAmt)}</div>
              </div>
            </div>

            <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
              {t("dashboard.surplusFormula2")}
            </div>

            {surplusAmt < 0 ? (
              <div style={{ marginTop: 8, fontSize: 12, color: "var(--bad)", fontWeight: 900 }}>
                Deficit: {money(surplusAmt)} ({pct(safeDiv(surplusAmt, allSummary.totalReceived))})
              </div>
            ) : null}

          </div>
        </div>
      </div>

      {/* Projects table (filters moved INTO this card header) */}
      <div className="card">
        <div className="cardHeader">
          <div>
            <h3 className="cardTitle">{t("dashboard.projectsTitle")}</h3>
            <div className="cardSub">
              {t("dashboard.projectsSub")}
            </div>
          </div>

          {projectsHeaderRight}
        </div>

        <div className="tableWrap" style={{ maxHeight: "60vh", overflow: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>{t("dashboard.colProject")}</th>
                <th>{t("dashboard.colEstimate")}</th>
                <th>{t("dashboard.colStatus")}</th>
                <th>{t("dashboard.colReceived")}</th>
                <th>{t("dashboard.colReceivable")}</th>
                <th>{t("dashboard.colWages")}</th>
                <th>{t("dashboard.colSubContracts")}</th>
                <th>{t("dashboard.colExpenses")}</th>
                <th>{t("dashboard.colNetProfit")}</th>
                <th>{t("dashboard.colMargin")}</th>
              </tr>
            </thead>
            <tbody>
              {projectRows.map((r: any) => {
                const receivableHot = r.receivable > 0;
                const profitBad = r.netProfit < 0;
                const marginBad = r.profitMargin < 0.25;

                const pretty = projectDisplayLabelPretty(
                  { id: r.id, clientId: r.clientId, address: r.address },
                  clients as any
                );

                return (
                  <tr
                    key={r.id}
                    style={r.needsAttention ? { background: "rgba(217,119,6,0.08)" } : undefined}
                  >
                    <td>
                      <Link to={`/projects/${r.id}`}>{pretty}</Link>

                      {r.needsAttention && (
                        <div style={{ fontSize: 12, color: "#8a5a00", marginTop: 4 }}>
                          {r.receivable > 0 ? `${t("dashboard.receivable", { amount: money(r.receivable) })}` : null}
                          {r.receivable > 0 &&
                          (r.outstandingSubBalances > 0 || r.unpaidWages > 0)
                            ? " • "
                            : null}
                          {r.outstandingSubBalances > 0
                            ? `${t("dashboard.subsDue", { amount: money(r.outstandingSubBalances) })}`
                            : null}
                          {r.outstandingSubBalances > 0 && r.unpaidWages > 0 ? " • " : null}
                          {r.unpaidWages > 0 ? `${t("dashboard.unpaidWagesLabel")}: ${money(r.unpaidWages)}` : null}
                        </div>
                      )}
                    </td>

                    <td>{money(r.totalEstimate)}</td>

                    <td
                      style={{
                        fontWeight: 900,
                        color: r.status === "Settled" ? "var(--good)" : "var(--primary)",
                      }}
                    >
                      {r.status}
                    </td>

                    <td>{money(r.received)}</td>

                    <td
                      className={receivableHot ? "numWarn" : "numGood"}
                      title={receivableHot ? t("dashboard.stillToCollect") : t("dashboard.fullyCollected")}
                    >
                      {money(r.receivable)}
                    </td>

                    <td>{money(r.wages)}</td>
                    <td>{money(r.subContractPayments)}</td>
                    <td>{money(r.otherExpenses)}</td>

                    <td className={profitBad ? "numBad" : "numGood"}>{money(r.netProfit)}</td>

                    <td
                      className={profitBad ? "numBad" : marginBad ? "numWarn" : "numGood"}
                      title={t("dashboard.profitMarginTip")}
                    >
                      {pct(r.profitMargin)}
                    </td>
                  </tr>
                );
              })}

              {projectRows.length === 0 && (
                <tr>
                  <td colSpan={10} style={{ textAlign: "center", color: "var(--muted)" }}>
                    {t("dashboard.noProjects")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 10 }}>
          {t("dashboard.needsAttentionNote")}
        </div>
      </div>
    </div>
  );
}

